import { useEffect, useState } from "react";
import { Autocomplete } from "@/components/Autocomplete";
import { placesAutocomplete, placesDetails, placesHealth } from "@/lib/orders";

function buildAddressLine(details: {
  formatted?: string;
  strasse?: string;
  hausnummer?: string;
  postleitzahl?: string;
  stadt?: string;
}): string {
  const formatted = (details.formatted || "").trim();
  if (formatted) return formatted;
  const street = [details.strasse, details.hausnummer].filter(Boolean).join(" ").trim();
  const city = [details.postleitzahl, details.stadt].filter(Boolean).join(" ").trim();
  return [street, city].filter(Boolean).join(", ");
}

function buildMapsSearchUrl(addressText: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText.trim())}`;
}

export interface PlacesAddressFieldProps {
  label?: string;
  hint?: string;
  value: string;
  onChange: (address: string, mapsUrl: string | null) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
}

export function PlacesAddressField({
  label = "Adresse",
  hint,
  value,
  onChange,
  placeholder = "Straße, PLZ Ort …",
  maxLength = 500,
  rows = 3,
}: PlacesAddressFieldProps) {
  const [placesOk, setPlacesOk] = useState(false);

  useEffect(() => {
    void placesHealth().then((h) => setPlacesOk(!!h?.available));
  }, []);

  return (
    <div className="space-y-2">
      {placesOk ? (
        <>
          <p className="text-xs text-label-tertiary">
            {hint ||
              "Adresse suchen und Vorschlag wählen – das Feld wird automatisch befüllt."}
          </p>
          <Autocomplete
            label="Adresse suchen"
            placeholder="Straße, PLZ, Ort …"
            minChars={2}
            clearOnSelect
            loadOptions={async (q) => {
              const list = await placesAutocomplete(q);
              return list.map((p) => ({
                key: p.place_id,
                label: p.text || [p.primary, p.secondary].filter(Boolean).join(", "),
                value: p.place_id,
              }));
            }}
            onSelect={async (opt) => {
              if (!opt) return;
              const d = await placesDetails(String(opt.value));
              const line = buildAddressLine(d);
              if (!line) return;
              onChange(line.slice(0, maxLength), buildMapsSearchUrl(line));
            }}
          />
        </>
      ) : (
        <p className="text-xs text-label-tertiary">
          Adress-Suche derzeit nicht verfügbar. Bitte Adresse manuell eingeben.
        </p>
      )}
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-label-secondary">{label}</span>
        <textarea
          className="eh-input min-h-[5.5rem] w-full resize-y"
          rows={rows}
          value={value}
          onChange={(e) => {
            const next = e.target.value.slice(0, maxLength);
            onChange(next, next.trim() ? buildMapsSearchUrl(next) : null);
          }}
          placeholder={placeholder}
          maxLength={maxLength}
        />
      </label>
    </div>
  );
}
